import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import type { Provider } from '@supabase/supabase-js'
import { toast } from 'sonner'
import { reportError } from '@/lib/error-reporter'
import { profile } from '@/lib/queries/profiles'
import { supabase } from '@/lib/supabase/client'

export function useAuthActions() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const signIn = async (
    email: string,
    password: string,
    redirectTo?: string,
    captchaToken?: string
  ) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: captchaToken ? { captchaToken } : undefined,
      })

      if (error) {
        // Provide user-friendly message for invalid credentials
        if (error.code === 'invalid_credentials') {
          throw new Error('Incorrect email or password')
        }
        throw new Error('Sign-in failed')
      }

      if (data.user) {
        // Fetch profile and cache it (router's beforeLoad will read from cache)
        const profileData = await queryClient.query(
          profile.findOne(data.user.id)
        )

        const displayName = profileData.full_name || email
        toast.success(`Welcome back, ${displayName}!`)
        // The return path can carry a query string (e.g. back to an
        // invitation), so it is followed as an href, same-site only.
        await navigate({ href: safeRedirectPath(redirectTo), replace: true })
      }

      return data
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Sign-in failed due to an error, try again or contact support.'
      toast.error(errorMessage)
    }
  }

  /**
   * Creates the account. With email confirmation on, no session comes back
   * until the 6-digit code is verified; an already-registered address looks
   * the same, so the response never reveals which emails have accounts.
   */
  const signUp = async (
    email: string,
    password: string,
    captchaToken?: string
  ): Promise<{ success: boolean; confirmed?: boolean }> => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { captchaToken },
    })

    if (error) {
      toast.error(
        error.code === 'weak_password'
          ? 'Choose a stronger password'
          : 'Could not create your account, try again.'
      )
      return { success: false }
    }

    if (data.session) {
      await navigate({ to: '/', replace: true })
      return { success: true, confirmed: true }
    }

    return { success: true, confirmed: false }
  }

  const verifySignupOtp = async (
    email: string,
    token: string
  ): Promise<{ success: boolean; error?: string }> => {
    const { error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    })

    if (error) {
      return {
        success: false,
        error: error.message || 'Invalid or expired code',
      }
    }

    return { success: true }
  }

  const resendSignupCode = async (email: string, captchaToken?: string) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: { captchaToken },
    })

    if (error) {
      toast.error('Could not resend the code, try again shortly.')
      return
    }

    toast.success('New code sent! Check your inbox.')
  }

  const signInWithProvider = async (
    provider: Extract<Provider, 'google'>,
    redirectTo?: string
  ) => {
    rememberPostAuthRedirect(redirectTo)
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    })

    if (error) {
      reportError(
        new Error(`OAuth sign-in with ${provider} failed`, { cause: error }),
        { toast: 'Could not start sign-in, try again.' }
      )
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut()

      if (error) {
        throw new Error('Sign-out failed')
      }

      // Clear React Query cache to remove stale user data
      queryClient.clear()

      // Navigate to sign-in using TanStack Router
      await navigate({ to: '/sign-in', replace: true })
    } catch {
      Object.keys(localStorage)
        .filter((key) => key.startsWith('sb-'))
        .forEach((key) => localStorage.removeItem(key))
      window.location.href = '/sign-in'
    }
  }

  const resetPasswordForEmail = async (
    email: string,
    captchaToken?: string
  ) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        captchaToken,
      })

      if (error) {
        throw new Error('Failed to send password reset email')
      }

      toast.success('Recovery code sent! Check your inbox.')
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to send password reset email'
      toast.error(errorMessage)
    }
  }

  const verifyRecoveryOtp = async (
    email: string,
    token: string
  ): Promise<{ success: boolean; error?: string }> => {
    const { error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'recovery',
    })

    if (error) {
      return {
        success: false,
        error: error.message || 'Invalid or expired code',
      }
    }

    return { success: true }
  }

  const updatePassword = async (
    newPassword: string,
    currentPassword?: string
  ): Promise<{
    success: boolean
    error?: string
  }> => {
    // If current password is provided, verify it first
    if (currentPassword) {
      const { data, error } = await supabase.rpc('verify_user_password', {
        password: currentPassword,
      })

      if (error) {
        // Report critical RPC failure to Sentry
        reportError(
          new Error('Password verification RPC failed', { cause: error }),
          { toast: false }
        )

        return {
          success: false,
          error:
            'System error, developers have been notified. Please try again later.',
        }
      }

      if (!data) {
        return {
          success: false,
          error: 'Current password is incorrect',
        }
      }
    }

    // Current password verified (if provided), proceed with update
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    })

    if (error) {
      return {
        success: false,
        error: error.message || 'Failed to update password',
      }
    }

    return { success: true }
  }

  return {
    signIn,
    signUp,
    verifySignupOtp,
    resendSignupCode,
    signInWithProvider,
    signOut,
    resetPasswordForEmail,
    verifyRecoveryOtp,
    updatePassword,
  }
}

/** An in-app path to return to after signing in; anything else goes home. */
export function safeRedirectPath(value: string | undefined): string {
  return value?.startsWith('/') &&
    !value.startsWith('//') &&
    !value.startsWith('/\\')
    ? value
    : '/'
}

const POST_AUTH_REDIRECT_KEY = 'lumoo:post-auth-redirect'

// The OAuth round trip leaves the app, and the provider's return URL must
// match the allow-list exactly, so the destination waits in session storage.
function rememberPostAuthRedirect(redirectTo: string | undefined) {
  try {
    if (redirectTo) {
      sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, redirectTo)
    } else {
      sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY)
    }
  } catch {
    // Storage blocked: the user lands on the home route instead.
  }
}

export function takePostAuthRedirect(): string {
  try {
    const value = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY)
    sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY)
    return safeRedirectPath(value ?? undefined)
  } catch {
    return '/'
  }
}
